/**
 * Чипсы для Help bottom sheet: короткий label на экране, развёрнутый текст в поле ввода по клику.
 * Ключи = topicKey из проекта (sosTopics). Обычно 4–5 чипсов на тему. Без автоотправки.
 *
 * Порядок quick-чипсов: сначала все free, затем premium (как на главной «Помощь маме»).
 */

export interface HelpChipItem {
  label: string;
  text: string;
  /** Для quick chips: free = доступно всем, paid = только Premium/Trial. */
  access?: "free" | "paid";
}

/** Чипсы для входа в консультацию без выбора темы (hero + chat sheet). */
const QUICK_HELP_CHIPS_RAW: HelpChipItem[] = [
  { label: "Не хочет есть", text: "Ребёнок не хочет есть — что делать?", access: "free" },
  { label: "Когда срочно к врачу", text: "Когда срочно обращаться к врачу?", access: "free" },
  { label: "Новый продукт", text: "Как безопасно ввести новый продукт?", access: "paid" },
  { label: "Стул малыша", text: "Стул малыша: норма или повод волноваться?", access: "paid" },
  { label: "Срыгивания", text: "Срыгивания: норма или проблема?", access: "paid" },
  { label: "Аллергия", text: "Аллергия или реакция — что делать?", access: "paid" },
  { label: "Режим кормления", text: "График кормления: подходит ли возрасту?", access: "paid" },
  { label: "Наша тарелка", text: "Наша тарелка: записать и получить совет", access: "paid" },
];

export const QUICK_HELP_CHIPS: HelpChipItem[] = [...QUICK_HELP_CHIPS_RAW];

/** Тексты prompt'ов, которые считаются Premium (для quick topic). Fail-safe: не отправлять Free пользователю. */
export function getPremiumQuickChipTexts(): string[] {
  return QUICK_HELP_CHIPS_RAW.filter((c) => c.access === "paid").map((c) => c.text);
}

/** Проверка: является ли текст prompt'ом премиум-чипа (нормализованное сравнение по trim). */
export function isPremiumQuickChipText(text: string): boolean {
  const t = text.trim();
  return QUICK_HELP_CHIPS_RAW.some((c) => c.access === "paid" && c.text.trim() === t);
}

export const HELP_TOPIC_CHIPS: Record<string, { label: string; text: string }[]> = {
  quick: QUICK_HELP_CHIPS,
  new_food: [
    { label: "Сколько в первый раз", text: "Сколько грамм нового продукта давать в первый раз? Как увеличивать порцию в следующие дни?" },
    { label: "Как понять, что нет аллергии", text: "Как понять, что у ребёнка нет аллергии на новый продукт? Сколько дней наблюдать и на что смотреть?" },
    { label: "Порядок ввода овощей", text: "В каком порядке лучше вводить овощи в прикорм? С чего начать и как комбинировать?" },
    { label: "Как ввести яйцо", text: "Как безопасно ввести яйцо в прикорм? С какого возраста и в каком виде лучше начать?" },
    { label: "Рыба: когда и как", text: "Можно ли давать рыбу в 8 месяцев и как начать? Какой сорт выбрать и сколько давать в первый раз?" },
  ],
  food_refusal: [
    { label: "Ест только пюре", text: "Ребёнок ест только пюре, а кусочки выплёвывает. Это нормально для его возраста или нужно что-то менять?" },
    { label: "Отказывается несколько дней", text: "Ребёнок отказывается от еды уже несколько дней. Что может быть причиной и что можно сделать дома?" },
    { label: "Резко перестал есть", text: "Раньше ел нормально, а сейчас резко отказывается от еды. С чем это может быть связано и что проверить?" },
    { label: "Плачет при кормлении", text: "Ребёнок плачет во время кормления. Это может быть из-за боли или дискомфорта? Что стоит попробовать?" },
  ],
  allergy: [
    { label: "Сыпь после продукта", text: "После нового продукта появилась сыпь. Похоже ли это на аллергию и что делать дальше?" },
    { label: "Покраснение вокруг рта", text: "После еды появилось покраснение вокруг рта. Это аллергия или раздражение, и нужно ли отменять продукт?" },
    { label: "Зуд и чешется", text: "Ребёнок чешется и появился зуд после еды. Что это может быть и какие действия правильные?" },
    { label: "Отличить аллергию", text: "Как отличить пищевую аллергию от раздражения кожи или реакции на контакт с продуктом?" },
  ],
  constipation_diarrhea: [
    { label: "Жидкий и частый", text: "Стул стал жидким и частым. Это может быть нормой или нужно обращать внимание на какие-то признаки?" },
    { label: "Запор несколько дней", text: "Запор уже несколько дней. Как помочь ребёнку мягко и безопасно, и когда стоит волноваться?" },
    { label: "Слизь в стуле", text: "Появилась слизь в стуле. Что это может означать и когда нужно обращаться к врачу?" },
    { label: "Изменился цвет", text: "Изменился цвет стула. Какие варианты бывают нормальными, а какие требуют внимания?" },
  ],
  spitting_up: [
    { label: "После каждого кормления", text: "Ребёнок срыгивает после каждого кормления. Это нормально или стоит что-то поменять?" },
    { label: "Большой объём", text: "Срыгивает довольно большим объёмом. Как понять, это допустимо или уже проблема?" },
    { label: "Через 1–2 часа", text: "Иногда срыгивает через 1–2 часа после еды. С чем это бывает связано и что можно сделать?" },
    { label: "Беспокоится после", text: "После срыгивания ребёнок беспокоится и плачет. На что обратить внимание и что попробовать?" },
  ],
  routine: [
    { label: "Сколько раз в день", text: "Сколько раз в день обычно должен есть ребёнок в этом возрасте? Как понять, что режим подходит?" },
    { label: "Ночные кормления", text: "Нужны ли ночные кормления сейчас или можно постепенно убирать? Как сделать это аккуратно?" },
    { label: "Сбился режим", text: "Сбился режим питания: то часто просит, то пропускает. Как мягко вернуть нормальный режим?" },
    { label: "Просит очень часто", text: "Ребёнок просит есть очень часто. Это нормально или может означать, что он не наедается?" },
  ],
  food_diary: [
    { label: "Хватает ли разнообразия", text: "Как понять, достаточно ли разнообразен рацион ребёнка? Что стоит добавить в ближайшие дни?" },
    { label: "Белок и железо", text: "Как понять, хватает ли белка и железа в питании? Какие продукты лучше добавить?" },
    { label: "Ест одно и то же", text: "Ребёнок ест почти одно и то же каждый день. Как расширять рацион без давления и стресса?" },
    { label: "Нужны ли витамины", text: "Нужны ли ребёнку витамины или добавки? Как понять, когда это действительно нужно обсудить?" },
  ],
  urgent_help: [
    { label: "Температура + не ест", text: "У ребёнка высокая температура и он отказывается от еды. Что можно сделать сейчас и когда нужно срочно обращаться?" },
    { label: "Рвота и понос", text: "Рвота и понос одновременно. Какие первые действия дома и какие признаки опасные?" },
    { label: "Резко пропал аппетит", text: "Резко пропал аппетит. Это может быть тревожным симптомом и на что обратить внимание?" },
    { label: "Когда срочно к врачу", text: "Какие симптомы у ребёнка считаются поводом срочно обратиться к врачу в таких ситуациях?" },
  ],
};

export function getChipsForTopic(topicKey: string): HelpChipItem[] {
  return HELP_TOPIC_CHIPS[topicKey] ?? [];
}
